import React, { useState, useEffect } from 'react';
import { FileText, Upload, CheckCircle, XCircle, Clock, AlertTriangle, Search, RefreshCw, Eye, RotateCcw, Download, Activity } from 'lucide-react';
import { fileProcessingAPI } from '../services/api';

const ManagementFileProcessing = () => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [selectedFile, setSelectedFile] = useState(null);
  const [showLogModal, setShowLogModal] = useState(false);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'failed': return <XCircle className="h-5 w-5 text-red-600" />;
      case 'processing': return <Activity className="h-5 w-5 text-blue-600 animate-spin" />;
      case 'pending': return <Clock className="h-5 w-5 text-yellow-600" />;
      default: return <AlertTriangle className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'success': return 'bg-green-100 text-green-800 border-green-200';
      case 'failed': return 'bg-red-100 text-red-800 border-red-200';
      case 'processing': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getFileType = (filename) => {
    if (!filename) return 'Unknown';
    if (filename.includes('Subscriber')) return 'Subscriber Data';
    if (filename.includes('Securityfee')) return 'Security Fee';
    return 'Unknown';
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Fetch file processing data from API (uses shared api with auth + same base URL)
  const fetchFiles = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fileProcessingAPI.list({
        limit: 100,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(sourceFilter !== 'all' && { source: sourceFilter })
      });

      if (response?.success && response?.data?.files) {
        const formattedFiles = response.data.files.map(file => ({
          ...file,
          source: file.source_operator || file.source,
          processingDuration: formatDuration(file.processingDuration)
        }));
        setFiles(formattedFiles);
      }
    } catch (err) {
      console.error('Error fetching files:', err);
      setError('Failed to load file processing data. Please try again.');
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async (fileId) => {
    try {
      const response = await fileProcessingAPI.retry(fileId);

      if (response?.success) {
        fetchFiles();
      }
    } catch (err) {
      console.error('Error retrying file:', err);
      alert('Failed to retry file processing. Please try again.');
    }
  };

  const handleViewLog = async (file) => {
    try {
      const response = await fileProcessingAPI.getLogs(file.id);

      if (response?.success && response?.data) {
        setSelectedFile({
          ...file,
          logs: response.data.logs || [],
          errorLog: response.data.errorLog || file.errorLog || file.errorMessage
        });
        setShowLogModal(true);
      } else {
        setSelectedFile(file);
        setShowLogModal(true);
      }
    } catch (err) {
      console.error('Error fetching logs:', err);
      setSelectedFile(file);
      setShowLogModal(true);
    }
  };

  // Fetch files on component mount and when filters change
  useEffect(() => {
    fetchFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on mount
  }, []);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchFiles();
    }, 30000);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- interval depends on fetchFiles
  }, [searchTerm, statusFilter, sourceFilter]);

  const filteredFiles = files.filter(file => {
    const matchesSearch = searchTerm === '' ||
      (file.filename || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (file.source || '').toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || file.status === statusFilter;
    const matchesSource = sourceFilter === 'all' || file.source === sourceFilter;

    return matchesSearch && matchesStatus && matchesSource;
  });

  const stats = {
    total: files.length,
    success: files.filter(f => f.status === 'success').length,
    failed: files.filter(f => f.status === 'failed').length,
    processing: files.filter(f => f.status === 'processing').length,
    pending: files.filter(f => f.status === 'pending').length,
  };

  const operators = [...new Set(files.map(f => f.source || 'Unknown'))].filter(Boolean);

  return (
    <main className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <FileText className="h-8 w-8 mr-3 text-blue-600" />
              File Processing Monitor
            </h1>
            <p className="text-gray-600 mt-2">Real-time monitoring of incoming CDR and subscriber data files</p>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={fetchFiles}
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
            <button className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 px-4 py-2 rounded-lg flex items-center space-x-2">
              <Download className="h-4 w-4" />
              <span>Export</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          {[
            { label: 'Total Files', value: stats.total, icon: <FileText className="h-5 w-5" />, color: 'bg-blue-500', textColor: 'text-blue-600' },
            { label: 'Success', value: stats.success, icon: <CheckCircle className="h-5 w-5" />, color: 'bg-green-500', textColor: 'text-green-600' },
            { label: 'Failed', value: stats.failed, icon: <XCircle className="h-5 w-5" />, color: 'bg-red-500', textColor: 'text-red-600' },
            { label: 'Processing', value: stats.processing, icon: <Activity className="h-5 w-5" />, color: 'bg-blue-500', textColor: 'text-blue-600' },
            { label: 'Pending', value: stats.pending, icon: <Clock className="h-5 w-5" />, color: 'bg-yellow-500', textColor: 'text-yellow-600' }
          ].map((stat, index) => (
            <div key={index} className="bg-white rounded-xl p-4 shadow-lg border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <div className={`p-2 rounded-lg ${stat.color} text-white`}>
                  {stat.icon}
                </div>
              </div>
              <p className={`text-2xl font-bold ${stat.textColor}`}>{stat.value}</p>
              <p className="text-sm text-gray-500">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
            <div className="flex items-center">
              <AlertTriangle className="h-5 w-5 text-red-600 mr-3" />
              <div>
                <h3 className="text-sm font-semibold text-red-800">Error</h3>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 mb-6">
          <div className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    placeholder="Search by filename or source operator..."
                  />
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="success">Success</option>
                  <option value="failed">Failed</option>
                  <option value="processing">Processing</option>
                  <option value="pending">Pending</option>
                </select>
                <select
                  value={sourceFilter}
                  onChange={(e) => setSourceFilter(e.target.value)}
                  className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                >
                  <option value="all">All Operators</option>
                  {operators.map(op => (
                    <option key={op} value={op}>{op}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Files Table */}
        <div className="bg-white rounded-xl shadow-lg border border-gray-200">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Upload className="h-5 w-5 text-gray-600" />
                <h2 className="text-lg font-semibold text-gray-900">File Processing Queue</h2>
              </div>
              <div className="text-sm text-gray-500">
                {filteredFiles.length} files found
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            {loading && files.length === 0 ? (
              <div className="text-center py-12">
                <RefreshCw className="h-12 w-12 text-cyan-500 mx-auto mb-4 animate-spin" />
                <p className="text-gray-500 font-medium">Loading file processing data...</p>
              </div>
            ) : (
              <>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Name / Source</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Upload Time</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Records</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredFiles.map(file => (
                      <tr key={file.id} className="hover:bg-gray-50 transition-colors duration-150">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="p-2 bg-cyan-100 rounded-lg">
                              <FileText className="h-5 w-5 text-cyan-600" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{file.filename}</div>
                              <div className="text-sm text-gray-500">
                                <span className="font-medium">{file.source}</span> • {getFileType(file.filename)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center text-sm text-gray-900">
                            <Clock className="h-4 w-4 text-gray-400 mr-2" />
                            {file.uploadTime}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {(file.recordCount || 0).toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-500">records</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{file.processingDuration}</div>
                        </td>
                        <td className="px-6 py-4">
                          <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadge(file.status)}`}>
                            {getStatusIcon(file.status)}
                            <span className="ml-2 capitalize">{file.status}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleViewLog(file)}
                              className="p-2 text-cyan-600 hover:text-cyan-800 hover:bg-cyan-50 rounded-lg transition-colors"
                              title="View Log"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            {file.status === 'failed' && (
                              <button
                                onClick={() => handleRetry(file.id)}
                                className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
                                title="Retry Processing"
                              >
                                <RotateCcw className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                {filteredFiles.length === 0 && !loading && (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 font-medium">No files found</p>
                    <p className="text-gray-400 text-sm">Try adjusting your search filters</p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-500">
                Auto-refresh every 30 seconds
              </div>
              <div className="flex items-center space-x-4">
                <button className="text-sm text-cyan-600 hover:text-cyan-700 font-medium">
                  View All Logs
                </button>
                <button className="text-sm text-cyan-600 hover:text-cyan-700 font-medium">
                  Configure Alerts
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Log Modal */}
        {showLogModal && selectedFile && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[80vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="p-2 bg-cyan-100 rounded-lg mr-3">
                      <FileText className="h-5 w-5 text-cyan-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900">Processing Log</h3>
                      <p className="text-gray-500 text-sm">{selectedFile.filename}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowLogModal(false)}
                    className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <span className="text-2xl">&times;</span>
                  </button>
                </div>
              </div>

              <div className="p-6">
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-sm text-gray-500">Upload Time</div>
                        <div className="text-sm font-medium text-gray-900">{selectedFile.uploadTime}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Processing Duration</div>
                        <div className="text-sm font-medium text-gray-900">{selectedFile.processingDuration}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Record Count</div>
                        <div className="text-sm font-medium text-gray-900">{(selectedFile.recordCount || 0).toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-500">Status</div>
                        <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadge(selectedFile.status)}`}>
                          <span className="capitalize">{selectedFile.status}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {selectedFile.status === 'failed' && selectedFile.errorLog && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <AlertTriangle className="h-5 w-5 text-red-600 mr-3 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-semibold text-red-800 mb-1">Error Details</h4>
                          <p className="text-sm text-red-700">{selectedFile.errorLog}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedFile.status === 'success' && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <div className="flex items-start">
                        <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5" />
                        <div>
                          <h4 className="text-sm font-semibold text-green-800 mb-1">Processing Complete</h4>
                          <p className="text-sm text-green-700">File processed successfully with {(selectedFile.recordCount || 0).toLocaleString()} records.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {selectedFile.logs && selectedFile.logs.length > 0 ? (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Processing Steps</h4>
                      <div className="space-y-2">
                        {selectedFile.logs.map((step, idx) => (
                          <div key={idx} className="flex items-start justify-between text-sm">
                            <div className="flex items-center">
                              {step.stepStatus === 'completed' && <CheckCircle className="h-4 w-4 text-green-500 mr-2 mt-0.5" />}
                              {step.stepStatus === 'failed' && <XCircle className="h-4 w-4 text-red-500 mr-2 mt-0.5" />}
                              {step.stepStatus === 'in_progress' && <Activity className="h-4 w-4 text-blue-500 mr-2 mt-0.5 animate-spin" />}
                              {step.stepStatus === 'pending' && <Clock className="h-4 w-4 text-yellow-500 mr-2 mt-0.5" />}
                              {step.stepStatus === 'skipped' && <AlertTriangle className="h-4 w-4 text-gray-400 mr-2 mt-0.5" />}
                              <div>
                                <div className="text-gray-900 font-medium">{step.stepName}</div>
                                {step.errorMessage && (
                                  <div className="text-red-600 text-xs mt-1">{step.errorMessage}</div>
                                )}
                              </div>
                            </div>
                            {step.durationSeconds > 0 && (
                              <div className="text-gray-500 text-xs">{step.durationSeconds}s</div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Processing Steps</h4>
                      <div className="space-y-2">
                        {['File validation', 'Header parsing', 'Record processing', 'Database insertion', 'Finalization'].map((step, idx) => (
                          <div key={idx} className="flex items-center text-sm text-gray-600">
                            <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                            {step}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-100 flex justify-end space-x-3">
                <button
                  onClick={() => setShowLogModal(false)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>
                {selectedFile.status === 'failed' && (
                  <button
                    onClick={() => {
                      handleRetry(selectedFile.id);
                      setShowLogModal(false);
                    }}
                    className="px-6 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-600 hover:to-blue-700 transition-all flex items-center space-x-2"
                  >
                    <RotateCcw className="h-4 w-4" />
                    <span>Retry Processing</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* File Format Reference */}
        <div className="mt-6 bg-gradient-to-r from-blue-50 to-cyan-100 border border-blue-200 rounded-xl p-6">
          <div className="flex items-start">
            <FileText className="h-6 w-6 text-blue-600 mr-3 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-blue-800 mb-2">Supported File Formats</h3>
              <div className="space-y-2 text-sm text-blue-700">
                <p><strong>Subscriber Files:</strong> LTC_Subscriber_YYYYMMDD.seq, UNITEL_Subscriber_YYYYMMDD.seq, ETL_Subscriber_YYYYMMDD.seq, TPLUS_Subscriber_YYYYMMDD.seq, BEST_Subscriber_YYYYMMDD.seq</p>
                <p><strong>Security Fee Files:</strong> LTC_Securityfee_YYYYMMDD.seq, UNITEL_Securityfee_YYYYMMDD.seq, ETL_Securityfee_YYYYMMDD.seq, TPLUS_Securityfee_YYYYMMDD.seq, BEST_Securityfee_YYYYMMDD.seq</p>
                <p className="mt-2">Files are automatically processed upon upload from operator mediation systems and CDR feeds.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default ManagementFileProcessing;
